import React, { type FC } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import Button from '@mui/material/Button';

import { useGetShoppingLists, usePostShoppingList, usePostShoppingListPositions } from 'api';
import type { IShoppingListListItem } from 'api/types';
import WiseSelect from 'components/wise/WiseSelect';

import styles from 'theme/components/wise/WiseAddToShoppingListForm/WiseAddToShoppingListForm.module.scss';

export type WiseAddToShoppingListFormProps = {
  items: {
    product_id?: number;
    unit_id?: number;
    quantity: number;
    image?: string;
    title?: string;
    index?: string;
    price_net_formatted?: string;
    price_gross_formatted?: string;
    currency?: string;
  }[];
  onCancel: () => void;
  onSuccess?: () => void;
};

const WiseAddToShoppingListForm: FC<WiseAddToShoppingListFormProps> = ({
  onCancel,
  onSuccess,
  items
}) => {
  const { t } = useTranslation();

  const { values, errors, setFieldValue, handleSubmit } = useFormik({
    initialValues: { shoppingListId: undefined as number | undefined },
    validationSchema: Yup.object().shape({
      shoppingListId: Yup.number().required(
        t('Wybierz lub stwórz listę zakupową przed dodaniem produktu')
      )
    }),
    onSubmit: () => {
      addPositionsToShoppingList({
        positions: items.map((item) => ({
          product_id: item.product_id,
          unit_id: item.unit_id,
          quantity: item.quantity
        }))
      });
    }
  });

  const { data: shoppingListsData, refetch: refetchShoppingListsData } = useGetShoppingLists({
    page: 1,
    limit: 999
  });

  const { mutate: addShoppingList } = usePostShoppingList({
    onSuccess: (data) => {
      setFieldValue('shoppingListId', data.data.id);
      refetchShoppingListsData();
    }
  });

  const { mutate: addPositionsToShoppingList, isLoading: isAddingProducts } =
    usePostShoppingListPositions(values.shoppingListId || 0, {
      onSuccess: () => {
        onSuccess?.();
        onCancel();
      }
    });

  return (
    <div className={classnames(styles.root, 'StylePath-Components-Wise-WiseAddToShoppingListForm')}>
      <div className={styles.productList}>
        {items.map((item) => (
          <div className={styles.product} key={item.product_id}>
            <div className={styles.details}>
              <div className={styles.image}>
                {item.image ? <img src={item.image} alt="product cover" /> : null}
              </div>
              <div className={styles.description}>
                {item.title}
                {item.index ? (
                  <span className={styles.small}>
                    {t('INDEX')}: <strong>{item.index}</strong>
                  </span>
                ) : null}
              </div>
            </div>
            <div className={styles.price}>
              <strong>
                {item.price_net_formatted} {item.currency}
              </strong>{' '}
              {t('netto')}
              <span className={styles.small}>
                <strong>
                  {item.price_gross_formatted} {item.currency}
                </strong>{' '}
                {t('brutto')}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div>
        <WiseSelect<IShoppingListListItem>
          placeholder={t('Wybierz listę zakupową lub wpisz nazwę nowej')}
          styleGroup="portal"
          controlId="wise-shopping-list-portal"
          optionFontSize={'14px'}
          onChange={(shoppingList) =>
            shoppingList && setFieldValue('shoppingListId', shoppingList.id)
          }
          onCreateOption={(label: string) =>
            addShoppingList({
              name: label,
              description: ''
            })
          }
          value={values.shoppingListId}
          options={
            shoppingListsData?.items.map((shoppingList) => ({
              value: shoppingList.id,
              label: shoppingList.name,
              item: shoppingList
            })) || []
          }
          error={errors.shoppingListId as string | undefined}
        />

        <div className={styles.actions}>
          <Button
            variant="contained"
            className={classnames(styles.button, styles.buttonGhost)}
            onClick={onCancel}
            disabled={isAddingProducts}>
            {t('Anuluj')}
          </Button>
          <Button
            variant="contained"
            className={classnames(styles.button, styles.buttonPrimary)}
            onClick={() => handleSubmit()}
            disabled={isAddingProducts}>
            {t('Dodaj do listy')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WiseAddToShoppingListForm;
